import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import type { IIconsVector, IToastType } from 'shared/ui'
import { errorHandler } from 'shared/api'
import { RequestCancelAxios } from 'shared/api/lib'
import { type ZIndex } from 'shared/constants/zIndex'
import { ContactIntegrationHubspotInfo } from 'entities/Integrations/model/ContactIntegrationHubspotInfo'
import { ContactIntegrationActiveCampaignInfo } from 'entities/Integrations/model/ContactIntegrationActiveCampaignInfo'
import { ContactIntegrationPipedriveInfo } from 'entities/Integrations/model/ContactIntegrationPipedriveInfo'
import { ContactIntegrationSalesforceInfo } from 'entities/Integrations/model/ContactIntegrationSalesforceInfo'
import { ContactIntegrationInfusionsoftInfo } from 'entities/Integrations/model/ContactIntegrationInfusionsoftInfo'
import {
  type IHubspotPropertiesObjectType,
  type IIntegrationTagCreateDTO,
  IntegrationKey,
  IntegrationsApi,
  type IResponseErrorIntegrationActivecampaignTagsApply,
  type IResponseErrorIntegrationInfusionsoftTagsApply,
  type IResponseIntegrationHubspotData,
  type IResponseIntegrationsContactsActiveCampaignInfo,
  type IResponseIntegrationsContactsSalesforceInfo,
} from 'entities/Integrations'
import type {
  IUiSettingsIntegrationKey,
  IUiSettingsIntegrationType,
} from 'entities/Users/api/types'
import type { Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IViewAllData, type IViewAllDataProps, type IViewTypes } from 'widgets/ContactsDetails'
import { EnumSyncType, EnumIntegrationTabs } from 'widgets/ContactsDetails/store/types'
import { DraggableIntegrationStore } from 'widgets/ContactsDetails/ui/ContactsIntegrations/store/draggableIntegrationStore'
import type { IBlocksTypes } from 'widgets/ContactsDetails/store/contactsDetailsGlobalStore'

type IContactsIntegrationsStoreConfig = {
  resetActiveAndCloseDetailsIsMobile?: () => void
  onOpenView: (value: IViewTypes) => void
  onCloseView: () => void
  ZIndex?: ZIndex
  blockKeyView: IBlocksTypes
  onSync?: (type: EnumSyncType.integration) => void
}

const emptyViewAllData = {
  deals: [],
  companies: [],
  opportunities: [],
  tickets: [],
}

export class ContactsIntegrationsStore {
  private _contactId: number | null = null
  private _view: IViewTypes | null = null
  private _loadingIds: Map<number, number> = new Map()
  private _loading = false
  private _unlinking = false
  private _currentContact: Contact | null = null

  private _hubspotInfoMap: Map<number, ContactIntegrationHubspotInfo> = new Map()
  private _activeCampaignInfoMap: Map<number, ContactIntegrationActiveCampaignInfo> = new Map()
  private _pipedriveInfoMap: Map<number, ContactIntegrationPipedriveInfo> = new Map()
  private _salesforceInfoMap: Map<number, ContactIntegrationSalesforceInfo> = new Map()
  private _infusionsoftInfoMap: Map<number, ContactIntegrationInfusionsoftInfo> = new Map()

  private _loadedFinish: Map<IntegrationKey, { [key in string]: boolean }> = new Map()

  private _integrationTab: EnumIntegrationTabs = EnumIntegrationTabs.Contact
  private _viewIntegrationKey: IntegrationKey | null = null
  private _viewAllData: IViewAllData = emptyViewAllData
  private _customTitleIcon: IIconsVector | null = null
  private _objectType: IHubspotPropertiesObjectType = 'contact'

  private _config: IContactsIntegrationsStoreConfig | null = null

  private _dndItemsMap: Map<string, DraggableIntegrationStore> = new Map()
  private _cancelRequest = new RequestCancelAxios()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._integrationTab = EnumIntegrationTabs.Contact
    this._viewIntegrationKey = null
  }

  initDnD = (
    integrationKey: IUiSettingsIntegrationKey,
    integrationTypesItems: {
      type: IUiSettingsIntegrationType
      defaultItems?: string[]
    }[]
  ) => {
    integrationTypesItems.forEach(({ type, defaultItems }) => {
      this._dndItemsMap.set(
        `${integrationKey}_${type}`,
        new DraggableIntegrationStore(integrationKey, type, defaultItems || [])
      )
    })
  }

  resetDnD = () => {
    this._dndItemsMap.forEach((item) => {
      item.dispose()
    })
  }

  getDnDItem = (
    integrationKey: IUiSettingsIntegrationKey,
    integrationType: IUiSettingsIntegrationType
  ) => {
    return this._dndItemsMap.get(`${integrationKey}_${integrationType}`)
  }

  setContactId = (id: number, contact?: Contact) => {
    this._contactId = id
    this._currentContact = contact ?? null
  }

  setView = (value: IViewTypes) => {
    if (this._view === value) return

    this._view = value
  }

  get selectedContact() {
    if (!this._contactId) return

    return this._entities.contactsStore.getItem(this._contactId) || this._currentContact
  }

  get view() {
    return this._view
  }

  get viewAllData() {
    return this._viewAllData
  }

  get customTitleIcon() {
    return this._customTitleIcon
  }

  get viewIntegrationKey() {
    return this._viewIntegrationKey
  }

  get config() {
    return this._config
  }

  get integrationTab() {
    return this._integrationTab
  }

  get viewAllCount() {
    if (this.view === 'companies') return this._viewAllData.companies.length
    if (this.view === 'deals') return this._viewAllData.deals.length
    if (this.view === 'opportunities') return this._viewAllData.opportunities.length
    if (this.view === 'tickets') return this._viewAllData.tickets.length

    return 0
  }

  get loading() {
    return this._loading
  }

  get unlinking() {
    return this._unlinking
  }

  get objectType() {
    return this._objectType
  }

  setConfig = (config: IContactsIntegrationsStoreConfig) => {
    this._config = config
  }

  setObjectType = (objectType: IHubspotPropertiesObjectType) => {
    this._objectType = objectType
  }

  openView = (
    viewType: IViewTypes,
    viewAllData: IViewAllDataProps,
    customTitleIcon: IIconsVector,
    viewIntegrationKey: IntegrationKey
  ) => {
    this._config?.onOpenView(viewType)
    this._customTitleIcon = customTitleIcon
    this._viewAllData = { ...this._viewAllData, ...viewAllData }
    this._viewIntegrationKey = viewIntegrationKey
  }

  closeView = () => {
    this._config?.onCloseView()
    this._customTitleIcon = null
    this._viewAllData = emptyViewAllData
    this._viewIntegrationKey = null
  }

  dispose = () => {
    this.resetDnD()

    this._loadingIds.clear()
    this._loadedFinish.clear()
    this._hubspotInfoMap.clear()
    this._activeCampaignInfoMap.clear()
    this._pipedriveInfoMap.clear()
    this._salesforceInfoMap.clear()
    this._infusionsoftInfoMap.clear()
    this._cancelRequest.reset()
  }

  handleIntegrationTab = (tab: EnumIntegrationTabs) => {
    this._integrationTab = tab
  }

  private _deleteContactIntegrationInfo = (id: number, key?: IntegrationKey | string) => {
    if (!key) return

    switch (key) {
      case IntegrationKey.hubspot: {
        this._hubspotInfoMap.delete(id)
        return
      }
      case IntegrationKey.salesforce: {
        this._salesforceInfoMap.delete(id)
        return
      }
      case IntegrationKey.activecampaign: {
        this._activeCampaignInfoMap.delete(id)
        return
      }
      case IntegrationKey.pipedrive: {
        this._pipedriveInfoMap.delete(id)
        return
      }
      case IntegrationKey.infusionsoft: {
        this._infusionsoftInfoMap.delete(id)
        return
      }
    }
  }

  onSync = () => {
    this._config?.onSync?.(EnumSyncType.integration)
  }

  unlinkContactsIntegrations = async (id: number, key?: IntegrationKey | string) => {
    try {
      this._unlinking = true

      const { data } = await IntegrationsApi.unlinkContactsByIdIntegrations(id)
      this._deleteContactIntegrationInfo(id, key)
      this._entities.contactsStore.updateItem(data)
      this.onSync()
    } catch (e) {
      console.log('ERROR: unlinkContactsIntegrations: ', e)
    } finally {
      this._unlinking = false
    }
  }

  updateLoadedFinish = (key: IntegrationKey, tab: string, status: boolean) => {
    const item = this._loadedFinish.get(key)

    if (item) {
      this._loadedFinish.set(key, {
        ...item,
        [tab]: status,
      })
    } else {
      this._loadedFinish.set(key, {
        [tab]: status,
      })
    }
  }

  getLoadedFinish = (key: IntegrationKey, tab: string) => {
    const item = this._loadedFinish.get(key)

    if (!item) return false

    return Boolean(item[tab])
  }

  createContactsIntegrationsActiveCampaignTag = async (reqData: IIntegrationTagCreateDTO) => {
    const message: {
      message: string
      status: IToastType
    } = {
      message: '',
      status: 'success',
    }

    try {
      const { data } = await IntegrationsApi.createContactsIntegrationsActiveCampaignTag(reqData)

      data.message = data?.message || ''

      return message
    } catch (e) {
      const err = e as Error
      const { type, error } = await errorHandler<IResponseErrorIntegrationActivecampaignTagsApply>(
        err
      )

      message.message = type === 'axios-error' ? error.response?.data.tag[0] || '' : ''
      message.status = 'error'

      return message
    }
  }

  createContactsIntegrationInfusionsoftTag = async (reqData: IIntegrationTagCreateDTO) => {
    try {
      const res = await IntegrationsApi.createContactsIntegrationsInfusionsoftTag(reqData)

      const data: {
        message: string
        status: IToastType
      } = {
        message: res?.data?.message || '',
        status: 'success',
      }

      return data
    } catch (e) {
      console.log('ERROR: createContactsIntegrationInfusionsoftTag: ', e)

      const err = e as Error
      const { type, error } = await errorHandler<IResponseErrorIntegrationInfusionsoftTagsApply>(
        err
      )

      const data: {
        message: string
        status: IToastType
      } = {
        message: '',
        status: 'error',
      }

      if (type === 'axios-error') {
        data.message = error?.response?.data?.tag?.[0] || ''
      }

      return data
    }
  }

  getContactsIntegrationInfusionsoftTags = async (value: string) => {
    try {
      const res = await IntegrationsApi.getContactsIntegrationsInfusionsoftTags(
        encodeURIComponent(value)
      )

      return res.data
    } catch (e) {
      console.log('ERROR: getContactsIntegrationInfusionsoftTags: ', e)
      return []
    }
  }

  initContactIntegrationHubspotInfo = async (
    contactId: number,
    use_contact_card_properties?: boolean
  ) => {
    if (this._loadingIds.has(contactId)) return

    const emptyData: IResponseIntegrationHubspotData = {
      deals: [],
      properties: {},
      companies: [],
      tickets: [],
    }

    try {
      runInAction(() => {
        this._loading = true
        this._loadingIds.set(contactId, contactId)
      })

      this._cancelRequest.init()

      const response = await IntegrationsApi.getContactsIntegrationsHubspotInfo(
        {
          id: contactId,
          use_contact_card_properties: use_contact_card_properties,
        },
        {
          cancelToken: this._cancelRequest.token,
        }
      )
      const data = response?.data

      if (!data) {
        const info = new ContactIntegrationHubspotInfo(emptyData)

        runInAction(() => {
          this._hubspotInfoMap.set(contactId, info)
        })

        return
      }

      const info = new ContactIntegrationHubspotInfo(data.data)

      runInAction(() => {
        this._hubspotInfoMap.set(contactId, info)
      })
    } catch (e) {
      console.log('ERROR: initContactIntegrationHubspotInfo: ', e)

      const info = new ContactIntegrationHubspotInfo(emptyData)

      runInAction(() => {
        this._hubspotInfoMap.set(contactId, info)
      })
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingIds.delete(contactId)
      })
    }
  }

  initContactIntegrationHubspotInfoByType = async (contactId: number) => {
    if (this._loadingIds.has(contactId)) return

    const objectType = this._objectType
    const emptyData: IResponseIntegrationHubspotData = {
      deals: [],
      properties: {},
      companies: [],
      tickets: [],
    }

    try {
      runInAction(() => {
        this._loading = true
        this._loadingIds.set(contactId, contactId)
      })

      const loaded = this.getLoadedFinish(IntegrationKey.hubspot, objectType)
      let propertiesResponse = emptyData.properties
      let dealsResponse = emptyData.deals
      let companiesResponse = emptyData.companies
      let ticketsResponse = emptyData.tickets

      this._cancelRequest.init()

      if (objectType === 'contact' && !loaded) {
        const response = await IntegrationsApi.getContactsIntegrationsHubspotContactInfo(
          contactId,
          {
            cancelToken: this._cancelRequest.token,
          }
        )
        propertiesResponse = response.data
      }

      if (objectType === 'deal' && !loaded) {
        const response = await IntegrationsApi.getContactsIntegrationsHubspotDealInfo(contactId, {
          cancelToken: this._cancelRequest.token,
        })
        dealsResponse = response.data
      }

      if (objectType === 'company' && !loaded) {
        const response = await IntegrationsApi.getContactsIntegrationsHubspotCompanyInfo(
          contactId,
          {
            cancelToken: this._cancelRequest.token,
          }
        )
        companiesResponse = response.data
      }

      if (objectType === 'ticket' && !loaded) {
        const response = await IntegrationsApi.getContactsIntegrationsHubspotTicketInfo(contactId, {
          cancelToken: this._cancelRequest.token,
        })
        ticketsResponse = response.data
      }

      this.updateLoadedFinish(IntegrationKey.hubspot, objectType, true)

      const info = this._hubspotInfoMap.get(contactId)

      if (info) {
        if (objectType === 'contact') {
          info.addProperties(propertiesResponse)
        }

        if (objectType === 'deal') {
          info.addDeals(dealsResponse)
        }

        if (objectType === 'company') {
          info.addCompanies(companiesResponse)
        }

        if (objectType === 'ticket') {
          info.addTickets(ticketsResponse)
        }
      } else {
        const currentData: IResponseIntegrationHubspotData = {
          deals: dealsResponse || emptyData.deals,
          properties: propertiesResponse || emptyData.properties,
          companies: companiesResponse || emptyData.companies,
          tickets: ticketsResponse || emptyData.tickets,
        }

        const info = new ContactIntegrationHubspotInfo(currentData)

        runInAction(() => {
          this._hubspotInfoMap.set(contactId, info)
        })
      }
    } catch (e) {
      console.log('ERROR: initContactIntegrationHubspotInfo: ', e)
      if (e instanceof AxiosError) {
        emptyData.error = {
          error: e.errors.getError('error') || '',
          type: objectType,
        }
      }
      const info = new ContactIntegrationHubspotInfo(emptyData)

      runInAction(() => {
        this._hubspotInfoMap.set(contactId, info)
      })
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingIds.delete(contactId)
      })
    }
  }

  initContactIntegrationActiveCampaignInfo = async (id: number) => {
    if (this._loadingIds.has(id)) return

    const emptyData: IResponseIntegrationsContactsActiveCampaignInfo = {
      deals: [],
      accounts: {
        id: '',
        name: '',
      },
      properties: {
        first_name: '',
        last_name: '',
        email: '',
        tags: [],
      },
    }

    const emptyInfo = new ContactIntegrationActiveCampaignInfo(emptyData)

    try {
      runInAction(() => {
        this._loading = true
        this._loadingIds.set(id, id)
      })

      this._cancelRequest.init()

      const response = await IntegrationsApi.getContactsIntegrationsActiveCampaignInfo(id, {
        cancelToken: this._cancelRequest.token,
      })
      const data = response?.data

      if (!data) {
        this._activeCampaignInfoMap.set(id, emptyInfo)
        return
      }

      const info = new ContactIntegrationActiveCampaignInfo(data)

      runInAction(() => {
        this._activeCampaignInfoMap.set(id, info)
      })
    } catch (e) {
      console.log('ERROR: initContactIntegrationActiveCampaignInfo: ', e)

      this._activeCampaignInfoMap.set(id, emptyInfo)
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingIds.delete(id)
      })
    }
  }

  initContactIntegrationPipedriveInfo = async (id: number) => {
    if (this._loadingIds.has(id)) return

    const emptyData = {
      contactId: id,
      deal: [],
      properties: null,
      organization: null,
    }

    const emptyInfo = new ContactIntegrationPipedriveInfo(emptyData)

    try {
      runInAction(() => {
        this._loading = true
        this._loadingIds.set(id, id)
      })

      this._cancelRequest.init()

      const response = await IntegrationsApi.getContactsIntegrationsPipedriveInfo(id, {
        cancelToken: this._cancelRequest.token,
      })
      const data = response?.data

      if (!data) {
        runInAction(() => {
          this._pipedriveInfoMap.set(id, emptyInfo)
        })
        return
      }

      const info = new ContactIntegrationPipedriveInfo(data)
      runInAction(() => {
        this._pipedriveInfoMap.set(id, info)
      })
    } catch (e) {
      console.log('ERROR: initContactIntegrationPipedriveInfo: ', e)

      this._pipedriveInfoMap.set(id, emptyInfo)
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingIds.delete(id)
      })
    }
  }

  initContactIntegrationSalesforceInfo = async (id: number) => {
    if (this._loadingIds.has(id)) return

    const emptyData: IResponseIntegrationsContactsSalesforceInfo = {
      properties: {
        first_name: '',
        last_name: '',
        email: '',
        object_type: 'contact',
      },
      opportunities: [],
    }

    const emptyInfo = new ContactIntegrationSalesforceInfo(emptyData)

    try {
      runInAction(() => {
        this._loading = true
        this._loadingIds.set(id, id)
      })

      this._cancelRequest.init()

      const response = await IntegrationsApi.getContactsIntegrationsSalesforceInfo(id, {
        cancelToken: this._cancelRequest.token,
      })
      const data = response?.data

      if (!data) {
        runInAction(() => {
          this._salesforceInfoMap.set(id, emptyInfo)
        })
        return
      }

      const info = new ContactIntegrationSalesforceInfo(data)
      runInAction(() => {
        this._salesforceInfoMap.set(id, info)
      })
    } catch (e) {
      console.log('ERROR: initContactIntegrationSalesforceInfo: ', e)

      this._salesforceInfoMap.set(id, emptyInfo)
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingIds.delete(id)
      })
    }
  }

  initContactIntegrationInfusionsoftInfo = async (id: number) => {
    if (this._loadingIds.has(id)) return
    const emptyInfo = new ContactIntegrationInfusionsoftInfo(null)

    try {
      runInAction(() => {
        this._loading = true
        this._loadingIds.set(id, id)
      })

      this._cancelRequest.init()

      const response = await IntegrationsApi.getContactsIntegrationsInfusionsoftInfo(id, {
        cancelToken: this._cancelRequest.token,
      })
      const data = response?.data

      if (!data) {
        runInAction(() => {
          this._infusionsoftInfoMap.set(id, emptyInfo)
        })
        return
      }

      const info = new ContactIntegrationInfusionsoftInfo(data)
      runInAction(() => {
        this._infusionsoftInfoMap.set(id, info)
      })
    } catch (e) {
      console.log('ERROR: initContactIntegrationInfusionsoftInfo: ', e)

      this._infusionsoftInfoMap.set(id, emptyInfo)
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingIds.delete(id)
      })
    }
  }

  getContactIntegrationHubspotInfo = (id: number) => {
    if (isNaN(id)) return

    return this._hubspotInfoMap.get(id)
  }

  getContactIntegrationActiveCampaignInfo = (id: number) => {
    return this._activeCampaignInfoMap.get(id)
  }

  getContactIntegrationPipedriveInfo = (id: number) => {
    return this._pipedriveInfoMap.get(id)
  }

  getContactIntegrationSalesforceInfo = (id: number) => {
    return this._salesforceInfoMap.get(id)
  }

  getContactIntegrationInfusionsoftInfo = (id: number) => {
    return this._infusionsoftInfoMap.get(id)
  }

  get zIndex() {
    return this._config?.ZIndex
  }

  get integrationBlockViewKey() {
    return this._config?.blockKeyView || 'integration'
  }

  get hasHubspotInfo() {
    return !!this._hubspotInfoMap.size
  }
}
